import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import HighchartsReact from "highcharts-react-official";
import Highcharts from 'highcharts';
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { callautopopulate } from "../../../services/callautopop";
import _ from "underscore";
import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";
import { Column } from "primereact/column";
import services from "../../../services/services";
import { getFullMonthSelected, getMonthName } from "../../../utils/Helper";
import { Urls } from "../../../services/Urls";
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ToastContext } from "../../../context/ToastContext";
import { decryptData } from "../../../utils/encrypt";

export const usePrecisionDashboard = () => {
    const { showInfo } = useContext(ToastContext);
    const tagRef = useRef(null);
   const [filterOpts, setFilterOpts] = useState({ lead: [], project: [], users: [], technology: [] });
    const [filterValues, setFilterValues] = useState({ lead: [], project: [], users: [], technology: [], date: new Date((new Date()).setMonth((new Date()).getMonth() - 1)), isChange: false });
    const [cardData, setCardData] = useState({ employee_data: [], hours_data: [], performance_data: [], stability_data: [] });
    const [swotView, setSwotView] = useState(false);
    const [loader, setLoader] = useState({ card: false, efforts: false, precision: false });
    const [dataTableColumns, setDataTableColumns] = useState({ team_efforts: { parent: [], child: [] }, precision: { parent: { type: ["Business", "Database Effort", "Technical Efficiency", "Time Efficiency", "Extra Efforts", "Overall Performance Rating"], inner_type: ["Rating", "Line Chart"], type_keys: ["bu_rank", "db_eff_rank", "tech_rank", "time_efficiency_rank", "extra_efforts", "overall_standing_rank"] }, child: [] } });
    const [dataTableData, setDataTableData] = useState({ team_efforts: { data: [], total_row: {} }, precision: [], swot: [] });

     useEffect(() => {
        callautopopulate("project_leads", "projects", "users", "technology", "user_info")?.then(res => {
            setFilterOpts((prev) => ({
                ...prev,
                lead: _.sortBy(res?.project_leads, "user_display_name"),
                project: _.sortBy(res?.projects, "project_name"),
                users: _.sortBy(res?.users, "user_display_name"),
                technology: _.sortBy(res?.technology, "technology_name")
            }))
            const lead = _.findWhere(res?.project_leads, { user_code: res?.user_info?.[0]?.user_code });
            !_.isEmpty(lead) && setFilterValues((prev) => ({ ...prev, lead: [lead] }));
            getPrecisionReport(null, null, lead);
        })
    }, [])

    useEffect(() => {
        setCardData((prev) => ({
            ...prev, performance_data: [{ name: "Dev", value: 70 }, { name: "Test", value: 50 }, { name: "Doc", value: 30 }, { name: "Sup", value: 60 },], stability_data: [{ name: "Dev", value: 60 }, { name: "Test", value: 40 }, { name: "Doc", value: 50 }, { name: "Sup", value: 50 },]
        }));

        // set precision grid
        const preciCols = { ...dataTableColumns?.precision?.parent };
        let precision = [];
        preciCols?.type?.forEach((types) =>
            precision = precision.concat([{ parent: types, name: "Rating" }, { parent: types, name: "Line Chart" }])
        );
        setDataTableColumns((prev) => ({ ...prev, precision: { parent: preciCols, child: precision } }));
    }, [])

    useEffect(() => {
        filterValues?.apiCall && getPrecisionReport()
    }, [filterValues.apiCall])

    const getPrecisionReport = async (key, date, lead) => {
        try {
            setLoader((prev) => ({ ...prev, precision: true, efforts: true, card: true }));
            setCardData((prev) => ({ ...prev, employee_data: [], hours_data: [] }));
            setDataTableColumns((prev) => ({ ...prev, team_efforts: { parent: [], child: [] } }));
            setDataTableData({ team_efforts: { data: [], total_row: {} }, precision: [], swot: [] });
            const selected_date = date ? getFullMonthSelected(date) : getFullMonthSelected(filterValues?.date);
            const payload = {
                "from_date": selected_date?.from_date?.toLocaleDateString("en-GB").split("/").reverse().join("-") || "",
                "to_date": selected_date?.to_date?.toLocaleDateString("en-GB").split("/").reverse().join("-") || "",
                "users": key === "user" ? "" : _.pluck(filterValues?.users, "user_code")?.toString(),
                "project_lead_user_code": key === "lead" ? "" : lead ? lead?.user_code?.toString() : _.pluck(filterValues?.project_leads, "user_code")?.toString(),
                "technologies": key === "tech" ? "" : _.pluck(filterValues?.technology, "technology_name")?.toString(),
                "project_ids": key === "project" ? "" : _.pluck(filterValues?.projects, "project_id")?.toString(),
            }
            const res = await services.getInvokeRemote("POST", Urls("GETPRECISIONREPORT"), payload);
            if (res) {
                const projectSummary = res?.body?.data?.project_summary?.[0];
                const issueTypeGroupWise = res?.body?.data?.issue_type_groupwise_logs;
                const teamEfforts = res?.body?.data?.team_effort_description;
                const precision = res?.body?.data?.current_precision?.map(emp => ({
                    ...emp,
                    strength: emp.strength ? decryptData(emp.strength) : "",
                    weakness: emp.weakness ? decryptData(emp.weakness) : "",
                    opportunity: emp.opportunity ? decryptData(emp.opportunity) : "",
                    threat: emp.threat ? decryptData(emp.threat) : "",
                    remark: emp.remark ? decryptData(emp.remark) : "",
                }));
                const previousMonthPrecision = res?.body?.data?.previous_month_precision;
                const currentMonth = (new Date(payload?.from_date))?.getMonth() + 1;

                let cardHoursSummary = [];
                _.filter(issueTypeGroupWise, { month_number: currentMonth })?.forEach((item) => {
                    const obj = {
                        name: item?.issue_type_group_name,
                        value: parseFloat(item?.logged_hours),
                        percentage: parseFloat(item?.logged_hours_percentage),
                        color: "#00b894", bg_color: "rgba(0, 184, 148, 0.2)"
                    }
                    cardHoursSummary.push(obj)
                })
                setCardData((prev) => ({ ...prev, "hours_data": cardHoursSummary, "employee_data": { count: projectSummary?.employee_count, leads: projectSummary?.leads_count, projects: projectSummary?.projects_count } }));
                setLoader((prev) => ({ ...prev, card: false }));

                // format team efforts data
                const currentMonthTeamsData = _.filter(teamEfforts, { month_number: currentMonth });
                const teamParents = _.sortBy(_.uniq(_.pluck(currentMonthTeamsData, "issue_type_group_name")));
                let teamChilds = [];
                teamParents?.forEach((group) => {
                    const data = _.filter(currentMonthTeamsData, { issue_type_group_name: group });
                    teamChilds.push(_.uniq(_.pluck(data, "issue_type_name")));
                })
                const effortCols = { type: teamParents, inner_type: teamChilds };
                const team = effortCols?.inner_type?.flatMap((types) => types?.map((name) => ({ name })))
                setDataTableColumns((prev) => ({ ...prev, team_efforts: { parent: effortCols, child: team } }));

                let teamsData = [];
                let teamTotalRowData = {};
                currentMonthTeamsData?.forEach((data) => {
                    let obj = {
                        user_display_name: data?.user_display_name
                    }
                    if (_.isEmpty(_.findWhere(teamsData, { user_display_name: data?.user_display_name }))) {
                        teamChilds?.flatMap(item => item)?.forEach((child) => {
                            const findData = _.findWhere(currentMonthTeamsData, { user_display_name: data?.user_display_name, issue_type_name: child });
                            const loggedHours = parseFloat(findData?.logged_hours || 0);
                            obj[child] = {
                                value: loggedHours,
                                percentage: parseFloat(findData?.logged_hours_percentage || 0),
                            }
                            if (!obj["Total"]) {
                                obj["Total"] = { value: findData?.logged_hours_total || 0, percentage: (parseFloat(findData?.logged_hours_percentage || 0)) };
                            } else {
                                obj["Total"] = { value: obj["Total"]?.value || (findData?.logged_hours_total || 0), percentage: Math.abs(parseFloat(obj["Total"]?.percentage + parseFloat(findData?.logged_hours_percentage || 0))) };
                            }
                            if (!teamTotalRowData[child]) {
                                teamTotalRowData[child] = { value: loggedHours }
                            } else {
                                teamTotalRowData[child] = { value: parseFloat(teamTotalRowData[child]?.value + loggedHours) };
                            }
                        })
                        teamsData.push(obj);
                    }
                })

                // set bottom total row for team efforts grid
                teamTotalRowData["Total"] = { value: 0 };
                _.pluck(teamsData, "Total")?.forEach((item) => {
                    teamTotalRowData["Total"].value = teamTotalRowData["Total"]?.value + (item?.value || 0);
                })
                setDataTableData((prev) => ({ ...prev, team_efforts: { data: _.sortBy(teamsData, "user_display_name"), total_row: teamTotalRowData } }));
                setLoader((prev) => ({ ...prev, efforts: false }));

                // format precision/SWOT data
                let precisionData = []
                const sortedPrecision = previousMonthPrecision?.sort((a, b) => {
                    if (a?.report_month < b?.report_month) return -1;
                    if (a?.report_month > b?.report_month) return 1;

                    // If report_month is the same, sort by report_year
                    return a?.report_year - b?.report_year;
                });

                let allMonths = _.uniq(_.pluck(sortedPrecision, "report_month"));
                allMonths.push(currentMonth);
                _.uniq(_.pluck(precision, "user_display_name"))?.forEach((user) => {
                    const currentMonthUserData = _.filter(precision, { user_display_name: user });
                    const userPreviousData = _.filter(previousMonthPrecision, { user_display_name: user })
                    currentMonthUserData?.forEach((curr) => {
                        let obj2 = {
                            user_display_name: user,
                            project_name: curr?.project_name || "Overall",
                            project_id: curr?.project_id || 0
                        }
                        const prevData = generatePreviousMonthData(obj2, allMonths, userPreviousData, currentMonthUserData);
                        obj2 = { ...obj2, ...prevData }
                        if (_.isEmpty(_.findWhere(precisionData, { project_id: obj2?.project_id, user_display_name: obj2?.user_display_name }))) {
                            precisionData?.push(obj2);
                        }
                    })
                })
                setDataTableData((prev) => ({ ...prev, precision: _.sortBy(precisionData, "user_display_name"), swot: _.sortBy(_.uniq(_.filter(precision, { report_month: currentMonth }), "user_display_name"), "user_display_name") }))
                setLoader((prev) => ({ ...prev, precision: false }));
                setFilterValues((prev) => ({ ...prev, apiCall: false }))
            }
        } catch (error) {
            console.error("Error: ", error);
            setFilterValues((prev) => ({ ...prev, apiCall: false }))
            setLoader((prev) => ({ ...prev, efforts: false, precision: false, card: false }));
        }
    }

    const generatePreviousMonthData = (obj, allMonths, userData, currentMonthUserData) => {
        let dataObj = {}
        const allData = userData?.concat(currentMonthUserData);
        dataTableColumns?.precision?.parent?.type?.forEach((type, index) => {
            // set previous month precision data for chart
            let prevMonthData = [];
            allMonths?.forEach((month) => {
                const findData = _.findWhere(allData, { report_month: month, project_id: obj?.project_id })?.[dataTableColumns?.precision?.parent?.type_keys?.[index]];
                if (findData) {
                    prevMonthData.push({ rank: parseInt(decryptData(findData)) || 0, month: month })
                }
            })

            // set current month rank for precision grid
            const currentMonthRank = _.findWhere(currentMonthUserData, { project_id: obj?.project_id })?.[dataTableColumns?.precision?.parent?.type_keys?.[index]];
            dataObj[type] = { rank: currentMonthRank ? decryptData(currentMonthRank) : "-" || "-", prev_data: prevMonthData }
        })
        return dataObj;
    }

    const formatTeamEffortsCell = (rowData, name) => {
        return (
            <div className="d-flex gap-1 align-items-center justify-content-end" style={{ fontSize: "0.75rem" }}>
                <div className="fw-bold">{rowData?.[name]?.value ? rowData?.[name]?.value + "h" : "-"}</div>
                {name !== "Total" ? <div style={{ fontSize: "0.7rem", alignSelf: "flex-end" }}>{rowData?.[name]?.percentage ? parseFloat(rowData?.[name]?.percentage?.toFixed(2)) + "%" : ""}</div> : ""}
                <div>{(rowData?.[name]?.up !== undefined && rowData?.[name]?.percentage) ? rowData?.[name]?.up ? <FontAwesomeIcon icon="fa-solid fa-arrow-up" className="text-success" /> : <FontAwesomeIcon icon="fa-solid fa-arrow-down" className="text-danger" /> : ""}</div>
            </div>
        )
    }

    const formatTeamsTotalRow = (name) => {
        return (
            <div className="d-flex gap-1 align-items-center justify-content-end" style={{ fontSize: "0.75rem" }}>
                <div className="fw-bold">{dataTableData?.team_efforts?.total_row?.[name]?.value ? parseFloat((dataTableData?.team_efforts?.total_row?.[name]?.value).toFixed(2)) + "h" : "-"}</div>
            </div>
        )
    }

    const formatPrecisionCell = (rowData, value) => {
        return (
            <div className="d-flex gap-1 align-items-center justify-content-center" style={{ fontSize: "0.75rem" }}>
                {value?.name === "Rating" ?
                    <div className="fw-semibold">{rowData?.[value?.parent]?.rank}</div> :
                    <div style={{ height: "30px", width: "80px", zIndex: 0 }}>
                        <HighchartsReact highcharts={Highcharts} options={generateOption(rowData, value?.parent)} containerProps={{ style: { height: "inherit", width: "90%" } }} />
                    </div>
                }
            </div>
        )
    }

    const formatSwotCell = (rowData) => {
        return (
            <div>
                <div>Remark: {rowData?.remark}</div>
                <div>S: {rowData?.strength}</div>
                <div>W: {rowData?.weakness}</div>
                <div>O: {rowData?.opportunity}</div>
                <div>T: {rowData?.threat}</div>
            </div>
        )
    }

    const generateOption = (rowdata, parent) => {
        return {
            chart: {
                type: "spline",
                backgroundColor: "transparent",
                height: 30,
                margin: [0, 0, 0, 0],
            },
            title: { text: null },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                categories: rowdata?.[parent]?.prev_data?.map(item => item?.month) || [],
                visible: false
            },
            yAxis: {
                visible: false,
                // reversed: true,
                min: 1,
                startOnTick: true,
            },
            tooltip: {
                enabled: true,
                outside: true,
                useHTML: true,
                formatter: function () {
                    const headerName = rowdata?.user_display_name || this.series.name;
                    const month = getMonthName(this?.key);
                    return `
                        <b>${headerName}</b> (${month})<br/>
                        <span style="font-size: 13px">${this.series.name} Rating: <b>${this.y}</b></span>
                    `;
                }
            },
            plotOptions: {
                series: {
                    marker: {
                        enabled: true,
                        radius: 3,
                        states: {
                            hover: {
                                radius: 4
                            }
                        }
                    },
                    lineWidth: 1.5
                }
            },
            series: [
                {
                    name: parent,
                    data: rowdata?.[parent]?.prev_data?.map(item => item?.rank) || [],
                    color: '#0984e3'
                }
            ]
        };
    };

    const dataTableHeaderFooter = {
        teamEffortsHeaderGroup: (
            <ColumnGroup>
                <Row>
                    <Column header="Employee" rowSpan={3} style={{ width: "15vw" }} headerClassName='precision-employee-column' />
                    {
                        dataTableColumns?.team_efforts?.parent?.type?.map((value, index) => {
                            const childCount = dataTableColumns?.team_efforts?.parent?.inner_type[index]?.length ?? 0;
                            if (childCount > 0) {
                                return <Column key={index} header={value} colSpan={childCount} headerClassName='text-center' />;
                            }
                            return <Column key={index} header={value} headerClassName='text-center' />;
                        })
                    }
                    <Column header="Total" rowSpan={3} headerClassName='text-center' />
                </Row>
                <Row>
                    {
                        dataTableColumns?.team_efforts?.child?.map((value, index) => {
                            return <Column key={index} header={value?.name} headerClassName='text-center' />
                        })
                    }
                </Row>
            </ColumnGroup>
        ),
        teamEffortsFooterGroup: (
            <ColumnGroup>
                <Row>
                    <Column footer="Total" footerClassName='precision-employee-column' />
                    {
                        dataTableColumns?.team_efforts?.child?.map((value, index) => {
                            return <Column key={index} footer={formatTeamsTotalRow(value?.name)} />
                        })
                    }
                    <Column footer={formatTeamsTotalRow("Total")} />
                </Row>
            </ColumnGroup>
        ),
        precisionHeaderGroup: (
            <ColumnGroup>
                <Row>
                    <Column header="Employee" rowSpan={3} style={{ width: "15vw" }} headerClassName='precision-employee-column' />
                    <Column header="Project" rowSpan={3} style={{ width: "10vw" }} />
                    {
                        dataTableColumns?.precision?.parent?.type?.map((value, index) => {
                            return <Column key={index} header={value} colSpan={2} style={{ minWidth: "15vw" }} headerClassName='text-center' />;
                        })
                    }
                </Row>
                <Row>
                    {
                        dataTableColumns?.precision?.child?.map((value, index) => {
                            return <Column key={index} header={value?.name} headerClassName='text-center' />
                        })
                    }
                </Row>
            </ColumnGroup>
        ),
    };

    const handleMonthChange = (val, date) => {
        if (val !== 0) {
            let month = parseInt(filterValues?.date?.getMonth()) + val;
            let year = parseInt(filterValues?.date?.getFullYear());
            if (month < 0) {
                month = 11;
                year -= 1;
            } else if (month > 11) {
                month = 0
                year += 1;
            }
            const newDate = new Date(year, month, 1);
            setFilterValues((prev) => ({ ...prev, date: newDate }));
            getPrecisionReport(null, newDate);
        } else {
            setFilterValues((prev) => ({ ...prev, date: date }));
            getPrecisionReport(null, date);
        }
    }

    const exportTeamEffortsToExcel = async () => {
        if (!dataTableData?.team_efforts?.data?.length) {
            showInfo("Info", 'No data to export');
            return;
        }
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Team Efforts');
        const centerStyle = {
            alignment: { horizontal: 'center', vertical: 'middle' }
        };
        const mainHeaderRow1 = worksheet.getRow(1);
        mainHeaderRow1.getCell(1).value = 'Employee Name';
        mainHeaderRow1.getCell(1).style = centerStyle;
        let colIndex = 2;
        dataTableColumns?.team_efforts?.parent?.type?.forEach((parent, parentIndex) => {
            const childCount = dataTableColumns?.team_efforts?.parent?.inner_type[parentIndex]?.length || 0;
            if (childCount > 0) {
                worksheet.mergeCells(1, colIndex, 1, colIndex + childCount - 1);
                mainHeaderRow1.getCell(colIndex).value = parent;
                mainHeaderRow1.getCell(colIndex).style = centerStyle;
                colIndex += childCount;
            }
        });
        worksheet.mergeCells(1, colIndex, 1, colIndex);
        mainHeaderRow1.getCell(colIndex).value = 'Total';
        mainHeaderRow1.getCell(colIndex).style = centerStyle;
        const subHeaderRow2 = worksheet.getRow(2);
        subHeaderRow2.getCell(1).value = 'Employee Name';

        let childColIndex = 2;
        dataTableColumns?.team_efforts?.child?.forEach(col => {
            subHeaderRow2.getCell(childColIndex).value = col?.name;
            subHeaderRow2.getCell(childColIndex).style = centerStyle;
            childColIndex++;
        });

        worksheet.mergeCells('A1:A2');
        worksheet.mergeCells(`${XLSX.utils.encode_col(childColIndex)}1:${XLSX.utils.encode_col(childColIndex)}2`);

        dataTableData.team_efforts.data.forEach((row, rowIndex) => {
            const dataRow = worksheet.getRow(3 + rowIndex);
            dataRow.getCell(1).value = row.user_display_name;

            let dataColIndex = 2;
            dataTableColumns?.team_efforts?.child?.forEach(col => {
                const cellData = row[col?.name];
                dataRow.getCell(dataColIndex).value = cellData
                    ? `${cellData.value}h (${cellData.percentage?.toFixed(2)}%)`
                    : '-';
                dataRow.getCell(dataColIndex).style = centerStyle;
                dataColIndex++;
            });

            const totalData = row.Total;
            dataRow.getCell(dataColIndex).value = totalData
                ? `${totalData.value}h (${totalData.percentage?.toFixed(2)}%)`
                : '-';
            dataRow.getCell(dataColIndex).style = centerStyle;
        });

        const totalRowIndex = 3 + dataTableData.team_efforts.data.length;
        const totalRow = worksheet.getRow(totalRowIndex);
        totalRow.getCell(1).value = 'Total';
        totalRow.getCell(1).style = centerStyle;

        let totalColIndex = 2;
        dataTableColumns?.team_efforts?.child?.forEach(col => {
            const totalCell = dataTableData.team_efforts.total_row[col?.name];
            totalRow.getCell(totalColIndex).value = totalCell ? `${totalCell.value}h` : '-';
            totalRow.getCell(totalColIndex).style = centerStyle;
            totalColIndex++;
        });

        const grandTotal = dataTableData.team_efforts.total_row.Total;
        totalRow.getCell(totalColIndex).value = grandTotal ? grandTotal.value : '-';
        totalRow.getCell(totalColIndex).style = centerStyle;

        const headers = ['Employee Name', ...dataTableColumns?.team_efforts?.child?.map(c => c.name), 'Total'];
        worksheet.columns = headers.map(header => ({ width: Math.max(15, header.length + 2) }));
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Team_Efforts_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const exportPrecisionSwotToExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Employee Project Rating');

        worksheet.columns = [
            { header: 'Employee Name', key: 'user_display_name', width: 25 },
            { header: 'Project Name', key: 'project_name', width: 30 },
            { header: 'Business Rating', key: 'bu_rank', width: 15 },
            { header: 'DB Rating', key: 'db_eff_rank', width: 15 },
            { header: 'Tech Efficiency', key: 'tech_rank', width: 18 },
            { header: 'Time Efficiency', key: 'time_rank', width: 18 },
            { header: 'Extra Efforts', key: 'extra_efforts', width: 15 },
            { header: 'Overall Rating', key: 'overall', width: 15 },
            { header: 'Remark', key: 'remark', width: 25 },
            { header: 'Strength', key: 'strength', width: 25 },
            { header: 'Weakness', key: 'weakness', width: 25 },
            { header: 'Opportunity', key: 'opportunity', width: 25 },
            { header: 'Threat', key: 'threat', width: 25 },
        ];

        const precisionData = dataTableData?.precision || [];
        const swotData = dataTableData?.swot || [];
        const grouped = precisionData.reduce((acc, item) => {
            if (!acc[item.user_display_name]) acc[item.user_display_name] = [];
            const swotMatch = swotData.find(s => s.user_display_name === item.user_display_name);
            acc[item.user_display_name].push({
                ...item,
                strength: swotMatch?.strength?.trim() || '-',
                weakness: swotMatch?.weakness?.trim() || '-',
                opportunity: swotMatch?.opportunity?.trim() || '-',
                threat: swotMatch?.threat?.trim() || '-',
                remark: swotMatch?.remark?.trim() || '-'
            });
            return acc;
        }, {});
        let currentRow = 2;

        Object.keys(grouped).forEach(employeeName => {
            const projects = grouped[employeeName];
            const startRow = currentRow;

            projects.forEach((proj, index) => {
                worksheet.addRow({
                    user_display_name: employeeName,
                    project_name: proj.project_name,
                    bu_rank: proj.Business?.rank || '-',
                    db_eff_rank: proj['Database Effort']?.rank || '-',
                    tech_rank: proj['Technical Efficiency']?.rank || '-',
                    time_rank: proj['Time Efficiency']?.rank || '-',
                    extra_efforts: proj['Extra Efforts']?.rank || '-',
                    overall: proj['Overall Performance Rating']?.rank || '-',
                    remark: proj.remark,
                    strength: proj.strength,
                    weakness: proj.weakness,
                    opportunity: proj.opportunity,
                    threat: proj.threat
                });
                currentRow++;
            });

            const endRow = currentRow - 1;
            if (startRow < endRow) {
                worksheet.mergeCells(`A${startRow}:A${endRow}`);
            }
            worksheet.getCell(`A${startRow}`).alignment = {
                vertical: 'middle',
                horizontal: 'center'
            };
        });

        worksheet.getRow(1).eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { horizontal: 'center' };
        });
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'precision_report.xlsx');
    }

    return useMemo(() => ({
        tagRef, filterOpts, filterValues, setFilterValues, cardData, swotView, setSwotView, dataTableColumns, dataTableData, dataTableHeaderFooter, formatTeamEffortsCell, formatPrecisionCell, formatSwotCell, loader, handleMonthChange, getPrecisionReport, exportTeamEffortsToExcel, exportPrecisionSwotToExcel
    }), [tagRef, filterOpts, filterValues, setFilterValues, cardData, swotView, setSwotView, dataTableColumns, dataTableData, dataTableHeaderFooter, formatTeamEffortsCell, formatPrecisionCell, formatSwotCell, loader, handleMonthChange, getPrecisionReport, exportTeamEffortsToExcel, exportPrecisionSwotToExcel])
}